import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import ImagePickerModal from "@/components/ImagePickerModal";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubContent,
  DropdownMenuSubTrigger, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Map, Plus, ExternalLink, Trash2, ArrowLeft, Eye, Loader2, CheckCircle2, Upload, Hotel, Plane, UtensilsCrossed, Info, Sparkles, MoreHorizontal, FileText, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

const TYPE_ICON = {
  activity: Sparkles,
  accommodation: Hotel,
  transfer: Plane,
  food: UtensilsCrossed,
  info: Info,
};
const TYPE_LABEL = {
  activity: "Experiencia",
  accommodation: "Alojamiento",
  transfer: "Traslado",
  food: "Gastronomía",
  info: "En tu día libre",
};

export default function ItinerariesPage() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importUrl, setImportUrl] = useState("");
  const [autoRewrite, setAutoRewrite] = useState(false);
  const [importBusy, setImportBusy] = useState(false);
  const [publishBusy, setPublishBusy] = useState(false);
  const [seoBusy, setSeoBusy] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [targetPostId, setTargetPostId] = useState("");
  const [wpDrafts, setWpDrafts] = useState([]);
  const [wpDraftsLoading, setWpDraftsLoading] = useState(false);
  const [specialists, setSpecialists] = useState([]);
  const [specialistSlug, setSpecialistSlug] = useState("");
  const [suggestedSpecialist, setSuggestedSpecialist] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);  // { id, title } pending delete
  const [deleteBusy, setDeleteBusy] = useState(false);
  // Image picker state — { open, target: 'hero' | { day: number, slot: 'hero'|gallery_index }, defaultAlt }
  const [picker, setPicker] = useState({ open: false, target: null, defaultAlt: "" });

  // Load the 8 Espíritu Travel specialists (once)
  useEffect(() => {
    api.get("/itineraries/specialists")
      .then((r) => setSpecialists(r.data || []))
      .catch(() => {});
  }, []);

  // Load existing WP `toal` posts (drafts + published) so the user can pick one to populate.
  const loadWpDrafts = async () => {
    setWpDraftsLoading(true);
    try {
      const r = await api.get("/itineraries/wp-drafts");
      setWpDrafts(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudieron cargar los posts de WP");
    } finally {
      setWpDraftsLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/itineraries");
        if (!cancelled) setList(r.data || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/itineraries");
      setList(r.data || []);
    } finally {
      setLoading(false);
    }
  };

  // Compute Travefy original images (hero + all event images) for the picker
  const travefyOriginalImages = () => {
    if (!selected) return [];
    const seen = new Set();
    const out = [];
    if (selected.hero_image) { seen.add(selected.hero_image); out.push(selected.hero_image); }
    for (const day of selected.days || []) {
      for (const ev of day.events || []) {
        for (const img of ev.images || []) {
          if (img && !seen.has(img)) { seen.add(img); out.push(img); }
        }
      }
    }
    return out;
  };

  // Apply the picker selection back to the right field
  const handlePickerSelect = ({ url, id, alt }) => {
    if (!selected || !picker.target) return;
    const t = picker.target;
    if (t === "hero") {
      setSelected((s) => ({
        ...s,
        hero_image: url,
        hero_image_id: id || null,  // WP attachment id (null for Travefy originals)
        hero_image_alt: alt || s.hero_image_alt || "",
      }));
      return;
    }
    // Day-related targets: { kind: 'day-hero'|'day-gallery'|'day-add', dayIdx, evIdx, slotIdx? }
    if (typeof t === "object") {
      setSelected((s) => {
        const days = (s.days || []).map((d, di) => {
          if (di !== t.dayIdx) return d;
          const events = (d.events || []).map((e, ei) => {
            if (ei !== t.evIdx) return e;
            const images = [...(e.images || [])];
            if (t.kind === "day-hero" || t.kind === "day-gallery") {
              // Replace at slot index
              images[t.slotIdx] = url;
            } else if (t.kind === "day-add") {
              images.push(url);
            }
            // Update alt text from picker if event has none yet
            const image_alt = (e.image_alt || "").trim() || alt || "";
            return { ...e, images, image_alt };
          });
          return { ...d, events };
        });
        return { ...s, days };
      });
    }
  };

  // Remove an image from a day event by index
  const removeDayImage = (dayIdx, evIdx, slotIdx) => {
    setSelected((s) => {
      const days = (s.days || []).map((d, di) => {
        if (di !== dayIdx) return d;
        const events = (d.events || []).map((e, ei) => {
          if (ei !== evIdx) return e;
          const images = (e.images || []).filter((_, i) => i !== slotIdx);
          return { ...e, images };
        });
        return { ...d, events };
      });
      return { ...s, days };
    });
  };

  // Build the picker default alt from a day target
  const pickerAltForDay = (dayIdx) => {
    const d = (selected?.days || [])[dayIdx];
    return d?.title || d?.raw_title || selected?.title || "";
  };

  const openDetail = async (it) => {
    try {
      const r = await api.get(`/itineraries/${it.id}`);
      setSelected(r.data);
      setPreviewHtml("");
      setTargetPostId(it.wp_post_id ? String(it.wp_post_id) : "");
      setSpecialistSlug(r.data.specialist_slug || "");
      // Get country-based suggestion
      try {
        const sg = await api.get(`/itineraries/${it.id}/specialist-suggestion`);
        setSuggestedSpecialist(sg.data || null);
      } catch { setSuggestedSpecialist(null); }
      loadWpDrafts();
    } catch (e) {
      toast.error("No se pudo cargar el itinerario");
    }
  };

  const submitImport = async () => {
    if (!importUrl.trim() || !importUrl.includes("travefy.com")) {
      return toast.error("Pega una URL de travefy.com/trip/itinerary/...");
    }
    setImportBusy(true);
    try {
      const r = await api.post("/itineraries/from-travefy", {
        url: importUrl.trim(),
        auto_rewrite: autoRewrite,
      });
      if (autoRewrite) {
        toast.success("Importado. Reescritura con IA en curso (60-90s)…");
      } else {
        toast.success("Itinerario importado");
      }
      setImporting(false);
      setImportUrl("");
      setList((cur) => [r.data, ...cur]);
      await openDetail(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo importar");
    } finally {
      setImportBusy(false);
    }
  };

  // Poll the open itinerary every 4s while the AI rewrite is in progress
  useEffect(() => {
    if (selected?.ai_rewrite_status !== "pending") return;
    const id = setInterval(async () => {
      try {
        const r = await api.get(`/itineraries/${selected.id}`);
        if (r.data.ai_rewrite_status !== "pending") {
          setSelected(r.data);
          load();
          if (r.data.ai_rewrite_status === "ready") {
            toast.success("Reescritura IA completada");
          } else if (r.data.ai_rewrite_status === "failed") {
            toast.error("La reescritura con IA falló — tienes el original en inglés");
          }
        }
      } catch (e) { /* keep polling */ }
    }, 4000);
    return () => clearInterval(id);
  }, [selected?.id, selected?.ai_rewrite_status]);

  const saveDay = async (dayIndex, patch) => {
    if (!selected) return;
    const newDays = selected.days.map((d, i) => (i === dayIndex ? { ...d, ...patch } : d));
    setSelected({ ...selected, days: newDays });
  };
  const saveEvent = async (dayIndex, evIndex, patch) => {
    if (!selected) return;
    const newDays = selected.days.map((d, i) => {
      if (i !== dayIndex) return d;
      const events = d.events.map((e, j) => (j === evIndex ? { ...e, ...patch } : e));
      return { ...d, events };
    });
    setSelected({ ...selected, days: newDays });
  };

  const generateSeo = async () => {
    if (!selected) return;
    setSeoBusy(true);
    try {
      // Persist any pending content edits first so the AI has the latest payload
      await persistAll();
      const r = await api.post(`/itineraries/${selected.id}/generate-seo`);
      setSelected(r.data.itinerary);
      toast.success(
        (r.data.updated_fields || []).length
          ? `SEO actualizado: ${(r.data.updated_fields || []).join(", ")}`
          : "SEO ya estaba completo"
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo generar SEO");
    } finally {
      setSeoBusy(false);
    }
  };

  const persistAll = async () => {
    if (!selected) return;
    const payload = {
      title: selected.title,
      subtitle: selected.subtitle,
      hero_intro: selected.hero_intro,
      top_reasons: selected.top_reasons,
      days: selected.days,
      hero_image: selected.hero_image,
      hero_image_alt: selected.hero_image_alt,
      hero_image_id: selected.hero_image_id,
      included_html: selected.included_html,
      excluded_html: selected.excluded_html,
      seo_title: selected.seo_title,
      seo_description: selected.seo_description,
      seo_focus_keyword: selected.seo_focus_keyword,
      seo_slug: selected.seo_slug,
    };
    try {
      const r = await api.patch(`/itineraries/${selected.id}`, payload);
      setSelected(r.data);
      toast.success("Guardado");
      load();
    } catch (e) {
      toast.error("No se pudo guardar");
    }
  };

  const showPreview = async () => {
    if (!selected) return;
    await persistAll();
    try {
      const r = await api.get(`/itineraries/${selected.id}/preview`);
      setPreviewHtml(r.data.html || "");
    } catch (e) {
      toast.error("No se pudo cargar la previa");
    }
  };

  const publish = async () => {
    if (!selected) return;
    const tid = targetPostId.trim();
    const msg = tid
      ? `Reemplazar el contenido del post WP #${tid} con este itinerario?\n\nNo se cambia el status (sigue draft/publish como esté), pero se sobrescribe el título y el contenido.`
      : `Crear borrador NUEVO en WordPress para "${selected.title}"?\n\nQuedará como DRAFT — tú lo revisas y publicas desde WP Admin cuando estés listo.`;
    if (!window.confirm(msg)) return;
    setPublishBusy(true);
    try {
      await persistAll();
      const body = { upload_hero: true };
      if (tid) body.target_post_id = Number(tid);
      const r = await api.post(`/itineraries/${selected.id}/publish-to-wp`, body);
      toast.success(tid ? `Post #${tid} actualizado` : "Borrador creado en WordPress");
      setSelected((cur) => ({
        ...cur,
        status: "published_draft",
        wp_post_id: r.data.id,
        wp_edit_link: r.data.edit_link,
        wp_link: r.data.link,
      }));
      setTargetPostId("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo publicar");
    } finally {
      setPublishBusy(false);
    }
  };

  const publishAsPage = async () => {
    if (!selected) return;
    const effectiveSlug = specialistSlug || suggestedSpecialist?.slug || "marina";
    const sp = specialists.find((s) => s.slug === effectiveSlug);
    const spName = sp?.name || effectiveSlug;
    const msg = selected.wp_page_id
      ? `Re-enviar este itinerario a la Specialist Page existente #${selected.wp_page_id}?\n\nSpecialist: ${spName}`
      : `Crear nueva página en /destinationspecialistcollection/ para "${selected.title}"?\n\nSpecialist: ${spName}\nQuedará como DRAFT — la revisas en WP Admin y publicas cuando estés listo.`;
    if (!window.confirm(msg)) return;
    setPublishBusy(true);
    try {
      // Persist the specialist choice first
      if (specialistSlug && specialistSlug !== selected.specialist_slug) {
        await api.patch(`/itineraries/${selected.id}`, { specialist_slug: specialistSlug });
      }
      await persistAll();
      const body = {
        upload_hero: true,
        status: "draft",
        specialist_slug: effectiveSlug,
      };
      if (selected.wp_page_id) body.target_page_id = selected.wp_page_id;
      const r = await api.post(`/itineraries/${selected.id}/publish-as-page`, body);
      toast.success(selected.wp_page_id ? `Página #${r.data.id} actualizada` : "Specialist Page creada en WordPress");
      setSelected((cur) => ({
        ...cur,
        status: "published_page",
        wp_page_id: r.data.id,
        wp_page_link: r.data.link,
        wp_page_edit_link: r.data.edit_link,
        specialist_slug: (r.data.specialist || {}).slug || effectiveSlug,
      }));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo crear la Specialist Page");
    } finally {
      setPublishBusy(false);
    }
  };

  const removeItin = (it) => {
    // Open the confirmation dialog; the actual DELETE happens in confirmDelete.
    setDeleteTarget({ id: it.id, title: it.title || "(sin título)" });
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    try {
      await api.delete(`/itineraries/${deleteTarget.id}`);
      toast.success(`"${deleteTarget.title}" borrado del CRM`);
      if (selected?.id === deleteTarget.id) setSelected(null);
      setDeleteTarget(null);
      load();
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || "Error al borrar";
      toast.error(detail);
    } finally {
      setDeleteBusy(false);
    }
  };

  // ---------- Detail view ----------
  if (selected) {
    return (
      <div className="max-w-5xl mx-auto p-6 lg:p-8">
        {/* ============ TOOLBAR ============ */}
        <TooltipProvider delayDuration={150}>
        <div className="mb-6 flex flex-wrap items-center gap-2">
          {/* Left: back button + status badge */}
          <Button
            variant="outline"
            onClick={() => setSelected(null)}
            className="rounded-sm border-[#E2DCD0]"
            data-testid="itinerary-back"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />Volver
          </Button>
          <Badge variant="outline" className={`rounded-sm text-[10px] tracking-wider uppercase ${selected.status === "published_page" ? "bg-[#2D6A4F]/8 border-[#2D6A4F] text-[#2D6A4F]" : selected.status === "published_draft" ? "bg-[#c19a6b]/10 border-[#c19a6b] text-[#8c7c5e]" : "bg-[#FFE01B]/10 border-[#8c7c5e] text-[#8c7c5e]"}`}>
            {selected.status === "published_page" ? "Publicado" : selected.status === "published_draft" ? "Draft TOAL" : "Pendiente"}
          </Badge>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Specialist selector (compact) */}
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <Select
                  value={specialistSlug || suggestedSpecialist?.slug || "marina"}
                  onValueChange={setSpecialistSlug}
                >
                  <SelectTrigger className="rounded-sm h-9 w-[190px] text-xs border-[#E2DCD0]" data-testid="specialist-select">
                    <SelectValue placeholder="Specialist" />
                  </SelectTrigger>
                  <SelectContent className="bg-white max-h-[300px]">
                    {!specialistSlug && suggestedSpecialist && (
                      <div className="px-2 py-1.5 text-[10px] text-[#8A8F98] italic border-b border-[#E2DCD0]">
                        Sugerido: {suggestedSpecialist.name}
                      </div>
                    )}
                    {specialists.map((s) => (
                      <SelectItem key={s.slug} value={s.slug} data-testid={`specialist-${s.slug}`}>
                        <span className="flex items-center gap-2">
                          <img src={s.photo} alt="" className="w-6 h-6 rounded-full object-cover" />
                          <span>
                            <span className="font-medium">{s.name}</span>
                            <span className="text-[10px] text-[#8A8F98] block leading-tight">{s.title}</span>
                          </span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </TooltipTrigger>
            <TooltipContent>Travel Specialist asignado (aparecerá en la página de WP)</TooltipContent>
          </Tooltip>

          {/* Preview */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button onClick={showPreview} variant="outline" size="icon" className="rounded-sm border-[#E2DCD0] h-9 w-9" data-testid="preview-button" aria-label="Previsualizar">
                <Eye className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Previsualizar cómo se verá en WordPress</TooltipContent>
          </Tooltip>

          {/* Edit in WP (if already published) */}
          {selected.wp_page_edit_link && (
            <Tooltip>
              <TooltipTrigger asChild>
                <a href={selected.wp_page_edit_link} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="icon" className="rounded-sm border-[#E2DCD0] h-9 w-9" data-testid="open-wp-page-editor" aria-label="Abrir en WP Admin">
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </a>
              </TooltipTrigger>
              <TooltipContent>Abrir esta página en el editor de WordPress</TooltipContent>
            </Tooltip>
          )}

          {/* PRIMARY ACTION: Publish / Re-publish */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                onClick={publishAsPage}
                disabled={publishBusy}
                className="bg-[#D95D39] hover:bg-[#c54e2e] text-white rounded-sm font-medium"
                data-testid="publish-as-page-button"
              >
                {publishBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                {selected.wp_page_id ? "Actualizar en WP" : "Publicar en WP"}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {selected.wp_page_id
                ? `Re-enviar a WordPress (página #${selected.wp_page_id})`
                : "Crear la página /destinationspecialistcollection/[slug]/ en WordPress"}
            </TooltipContent>
          </Tooltip>

          {/* More options menu */}
          <DropdownMenu>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" className="rounded-sm border-[#E2DCD0] h-9 w-9" data-testid="more-options-trigger" aria-label="Más opciones">
                    <MoreHorizontal className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent>Más acciones (legacy / target post / refresh)</TooltipContent>
            </Tooltip>
            <DropdownMenuContent align="end" className="bg-white w-[280px]">
              <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-[#8A8F98]">Acciones avanzadas</DropdownMenuLabel>
              <DropdownMenuSeparator />

              {/* Target post submenu */}
              <DropdownMenuSub>
                <DropdownMenuSubTrigger data-testid="target-post-submenu">
                  <FileText className="w-4 h-4 mr-2" />
                  Target post:&nbsp;
                  <span className="text-[#D95D39] truncate">
                    {targetPostId ? `#${targetPostId}` : "nuevo borrador"}
                  </span>
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="bg-white w-[260px] max-h-[320px] overflow-y-auto">
                  <DropdownMenuItem onClick={() => setTargetPostId("")} data-testid="wp-draft-option-new">
                    <Plus className="w-3.5 h-3.5 mr-2 text-[#D95D39]" />
                    Crear borrador nuevo
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {wpDraftsLoading && (
                    <DropdownMenuItem disabled className="text-xs text-[#8A8F98]">Cargando…</DropdownMenuItem>
                  )}
                  {!wpDraftsLoading && wpDrafts.length === 0 && (
                    <DropdownMenuItem disabled className="text-xs text-[#8A8F98]">Sin posts existentes</DropdownMenuItem>
                  )}
                  {wpDrafts.map((p) => (
                    <DropdownMenuItem
                      key={p.id}
                      onClick={() => setTargetPostId(String(p.id))}
                      data-testid={`wp-draft-option-${p.id}`}
                      className="flex items-center gap-2 text-xs"
                    >
                      <Badge
                        variant="outline"
                        className={`rounded-sm text-[9px] px-1 py-0 ${
                          p.status === "publish"
                            ? "border-[#2D6A4F] text-[#2D6A4F]"
                            : "border-[#c19a6b] text-[#c19a6b]"
                        }`}
                      >
                        {p.status === "publish" ? "PUB" : "DRAFT"}
                      </Badge>
                      <span className="truncate" dangerouslySetInnerHTML={{ __html: p.title }} />
                      <span className="text-[10px] text-[#8A8F98] ml-auto">#{p.id}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>

              <DropdownMenuItem onClick={loadWpDrafts} data-testid="wp-drafts-refresh">
                <RefreshCw className="w-4 h-4 mr-2" />
                Recargar lista de posts WP
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-[#8A8F98]">Legacy (CPT toal)</DropdownMenuLabel>
              <DropdownMenuItem onClick={publish} disabled={publishBusy} data-testid="publish-button">
                <Upload className="w-4 h-4 mr-2" />
                {targetPostId ? `Actualizar TOAL #${targetPostId}` : "Crear draft TOAL (legacy)"}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        </TooltipProvider>

        {/* HERO meta */}
        <Card className="p-6 mb-6 rounded-sm border-[#E2DCD0] shadow-none">
          <Label className="text-xs">Título</Label>
          <Input
            value={selected.title || ""}
            onChange={(e) => setSelected({ ...selected, title: e.target.value })}
            className="mt-1 rounded-sm font-display text-2xl"
            data-testid="itinerary-title"
          />
          <Label className="text-xs mt-4 block">Subtítulo</Label>
          <Input
            value={selected.subtitle || ""}
            onChange={(e) => setSelected({ ...selected, subtitle: e.target.value })}
            className="mt-1 rounded-sm italic text-[#5c4e3a]"
            placeholder="Frase sensorial corta"
            data-testid="itinerary-subtitle"
          />
          <Label className="text-xs mt-4 block">Intro de la cabecera</Label>
          <Textarea
            value={selected.hero_intro || ""}
            onChange={(e) => setSelected({ ...selected, hero_intro: e.target.value })}
            className="mt-1 rounded-sm"
            rows={3}
            data-testid="itinerary-hero-intro"
          />
          <div className="grid grid-cols-2 gap-3 mt-4">
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs">Imagen hero</Label>
                <button
                  type="button"
                  onClick={() => setPicker({ open: true, target: "hero", defaultAlt: selected.title || "" })}
                  className="text-[10px] uppercase tracking-[0.15em] text-[#D95D39] hover:text-[#c54e2e] font-medium"
                  data-testid="hero-image-change-button"
                >
                  Cambiar imagen ↗
                </button>
              </div>
              <Input
                value={selected.hero_image || ""}
                onChange={(e) => setSelected({ ...selected, hero_image: e.target.value })}
                className="mt-1 rounded-sm text-xs font-mono"
                placeholder="https://espiritutravel.com/wp-content/uploads/..."
              />
              <Input
                value={selected.hero_image_alt || ""}
                onChange={(e) => setSelected({ ...selected, hero_image_alt: e.target.value })}
                className="mt-1 rounded-sm text-xs"
                placeholder="Alt text (SEO) — describe la imagen brevemente"
                data-testid="hero-image-alt-input"
              />
              {selected.hero_image && (
                <div className="mt-2 relative group">
                  <img src={selected.hero_image} alt={selected.hero_image_alt || ""} className="w-full h-32 object-cover rounded-sm" />
                  <button
                    type="button"
                    onClick={() => setPicker({ open: true, target: "hero", defaultAlt: selected.title || "" })}
                    className="absolute inset-0 bg-[#0A1128]/0 group-hover:bg-[#0A1128]/60 text-white opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center text-xs uppercase tracking-[0.18em] font-medium"
                  >
                    Cambiar imagen
                  </button>
                </div>
              )}
            </div>
            <div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Duración</Label>
                  <Input value={selected.duration_days || ""} readOnly className="mt-1 rounded-sm bg-[#F2EFE6]" />
                </div>
                <div>
                  <Label className="text-xs">Precio desde</Label>
                  <Input value={selected.price_from || ""} readOnly className="mt-1 rounded-sm bg-[#F2EFE6]" />
                </div>
              </div>
              {selected.source_url && (
                <a href={selected.source_url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#8A8F98] hover:text-[#D95D39] mt-3 inline-flex items-center gap-1">
                  <ExternalLink className="w-3 h-3" />Ver origen en Travefy
                </a>
              )}
            </div>
          </div>
        </Card>

        {/* Top reasons */}
        <Card className="p-6 mb-6 rounded-sm border-[#E2DCD0] shadow-none">
          <h3 className="font-display text-xl mb-3">Razones para hacer este viaje</h3>
          <div className="space-y-2">
            {(selected.top_reasons || []).map((r, idx) => (
              <Input
                key={idx}
                value={r}
                onChange={(e) => {
                  const arr = [...(selected.top_reasons || [])];
                  arr[idx] = e.target.value;
                  setSelected({ ...selected, top_reasons: arr });
                }}
                className="rounded-sm"
              />
            ))}
            <Button
              variant="outline"
              onClick={() => setSelected({ ...selected, top_reasons: [...(selected.top_reasons || []), ""] })}
              className="rounded-sm border-dashed border-[#8A8F98] text-[#8A8F98]"
            >
              <Plus className="w-4 h-4 mr-1" />Añadir razón
            </Button>
          </div>
        </Card>

        {/* AI rewrite progress banner */}
        {selected.ai_rewrite_status === "pending" && (
          <div className="bg-[#FAF7F2] border border-[#D95D39]/30 rounded-sm p-4 mb-4 flex items-center gap-3" data-testid="ai-rewrite-banner">
            <Loader2 className="w-5 h-5 text-[#D95D39] animate-spin flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-[#0A1128]">Reescribiendo con IA en segundo plano…</p>
              <p className="text-xs text-[#8A8F98] mt-0.5">Tarda 60-90s. Puedes seguir editando, los cambios manuales se conservan al terminar.</p>
            </div>
          </div>
        )}
        {selected.ai_rewrite_status === "failed" && selected.ai_rewrite_error && (
          <div className="bg-[#c54e2e]/5 border border-[#c54e2e]/30 rounded-sm p-4 mb-4 text-xs text-[#c54e2e]" data-testid="ai-rewrite-error">
            <strong>La reescritura con IA falló:</strong> {selected.ai_rewrite_error}
          </div>
        )}

        {/* SEO Panel — meta title, meta description, slug, focus keyword */}
        <Card className="p-6 mb-4 rounded-sm border-[#E2DCD0] shadow-none">
          <div className="flex items-baseline justify-between mb-3">
            <h3 className="font-display text-xl text-[#0A1128]">SEO</h3>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={generateSeo}
                disabled={seoBusy}
                className="rounded-sm border-[#E2DCD0] text-xs"
                data-testid="seo-regenerate-button"
              >
                {seoBusy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" strokeWidth={1.5} />}
                {seoBusy ? "Generando..." : "Generar con IA"}
              </Button>
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#8A8F98]">Para Google</span>
            </div>
          </div>
          <p className="text-xs text-[#8A8F98] mb-4">
            Estos campos se generan automáticamente con IA al importar de Travefy. Puedes editarlos manualmente o pulsar &quot;Generar con IA&quot; para reescribirlos.
          </p>
          <div className="space-y-3">
            <div>
              <Label className="text-xs flex items-center justify-between">
                <span>SEO Title <span className="text-[#8A8F98]">(60 caracteres recomendado)</span></span>
                <span className={`text-[10px] ${(selected.seo_title || "").length > 60 ? "text-[#c54e2e]" : "text-[#8A8F98]"}`}>
                  {(selected.seo_title || "").length}/60
                </span>
              </Label>
              <Input
                value={selected.seo_title || ""}
                onChange={(e) => setSelected({ ...selected, seo_title: e.target.value })}
                placeholder={`Ej: ${selected.title?.slice(0, 50) || "Portugal Tour — 7 Days"} | Espíritu Travel`}
                className="mt-1 rounded-sm text-sm"
                data-testid="seo-title-input"
              />
            </div>
            <div>
              <Label className="text-xs flex items-center justify-between">
                <span>Meta description <span className="text-[#8A8F98]">(150-160 caracteres)</span></span>
                <span className={`text-[10px] ${(selected.seo_description || "").length > 160 ? "text-[#c54e2e]" : "text-[#8A8F98]"}`}>
                  {(selected.seo_description || "").length}/160
                </span>
              </Label>
              <Textarea
                value={selected.seo_description || ""}
                onChange={(e) => setSelected({ ...selected, seo_description: e.target.value })}
                placeholder="Describe el viaje en 1-2 frases con la keyword principal incluida."
                className="mt-1 rounded-sm text-sm min-h-[72px]"
                data-testid="seo-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Focus keyword</Label>
                <Input
                  value={selected.seo_focus_keyword || ""}
                  onChange={(e) => setSelected({ ...selected, seo_focus_keyword: e.target.value })}
                  placeholder="Ej: portugal luxury tour"
                  className="mt-1 rounded-sm text-sm"
                  data-testid="seo-focus-input"
                />
              </div>
              <div>
                <Label className="text-xs">Slug URL</Label>
                <Input
                  value={selected.seo_slug || ""}
                  onChange={(e) => setSelected({ ...selected, seo_slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-").replace(/-+/g, "-") })}
                  placeholder="7-days-portugal-timeless-cities"
                  className="mt-1 rounded-sm text-sm font-mono"
                  data-testid="seo-slug-input"
                />
              </div>
            </div>
          </div>
          {/* SERP preview */}
          {(selected.seo_title || selected.seo_description) && (
            <div className="mt-4 pt-4 border-t border-[#E2DCD0]">
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#8A8F98] mb-2">Vista previa en Google</p>
              <div className="bg-white p-3 rounded-sm border border-[#E2DCD0]">
                <p className="text-xs text-[#2D6A4F] truncate">https://espiritutravel.com/destinationspecialistcollection/{selected.seo_slug || "..."}</p>
                <p className="text-[#1A0DAB] text-base mt-0.5 truncate" style={{fontFamily:"arial,sans-serif"}}>
                  {selected.seo_title || `${selected.title} | Espíritu Travel`}
                </p>
                <p className="text-[#545454] text-xs mt-1 line-clamp-2" style={{fontFamily:"arial,sans-serif"}}>
                  {selected.seo_description || selected.hero_intro || ""}
                </p>
              </div>
            </div>
          )}
        </Card>
        <h2 className="font-display text-2xl text-[#0A1128] mb-3">El viaje día a día</h2>
        {(selected.days || []).map((day, di) => (
          <Card key={di} className="p-6 mb-4 rounded-sm border-[#E2DCD0] shadow-none">
            <div className="flex items-baseline gap-3 mb-3">
              <span className="text-xs uppercase tracking-[0.18em] text-[#c19a6b] font-semibold">Día {day.number}</span>
              <Input
                value={day.title || ""}
                onChange={(e) => saveDay(di, { title: e.target.value })}
                className="rounded-sm font-display text-xl flex-1"
              />
            </div>
            <Textarea
              value={day.intro || ""}
              onChange={(e) => saveDay(di, { intro: e.target.value })}
              placeholder="Intro del día"
              className="rounded-sm italic"
              rows={2}
            />
            <div className="space-y-3 mt-3">
              {(day.events || []).map((ev, ei) => {
                const Icon = TYPE_ICON[ev.type] || Sparkles;
                return (
                  <div key={ei} className="border-t border-dashed border-[#E2DCD0] pt-3">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.16em] text-[#c19a6b]">
                        <Icon className="w-3.5 h-3.5" />{TYPE_LABEL[ev.type] || "Experiencia"}
                      </span>
                    </div>
                    <Input
                      value={ev.name || ""}
                      onChange={(e) => saveEvent(di, ei, { name: e.target.value })}
                      className="rounded-sm font-medium"
                    />
                    <Textarea
                      value={ev.description_html || ""}
                      onChange={(e) => saveEvent(di, ei, { description_html: e.target.value })}
                      className="rounded-sm mt-2 text-sm"
                      rows={4}
                    />
                    {/* Image manager — change existing thumbs, add new ones */}
                    <div className="mt-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] uppercase tracking-[0.16em] text-[#8A8F98]">
                          {ev.images?.length || 0} imágenes
                        </span>
                        <button
                          type="button"
                          onClick={() => setPicker({
                            open: true,
                            target: { kind: "day-add", dayIdx: di, evIdx: ei },
                            defaultAlt: pickerAltForDay(di),
                          })}
                          className="text-[10px] uppercase tracking-[0.15em] text-[#D95D39] hover:text-[#c54e2e] font-medium"
                          data-testid={`day-${di}-ev-${ei}-add-image`}
                        >
                          + Añadir imagen
                        </button>
                      </div>
                      <div className="grid grid-cols-4 gap-2">
                        {(ev.images || []).map((u, i) => (
                          <div key={i} className="relative group aspect-[4/3] overflow-hidden rounded-sm bg-[#F1ECE2]">
                            <img src={u} alt="" className="w-full h-full object-cover" />
                            <div className="absolute inset-0 bg-[#0A1128]/0 group-hover:bg-[#0A1128]/70 transition-all flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
                              <button
                                type="button"
                                onClick={() => setPicker({
                                  open: true,
                                  target: { kind: i === 0 ? "day-hero" : "day-gallery", dayIdx: di, evIdx: ei, slotIdx: i },
                                  defaultAlt: pickerAltForDay(di),
                                })}
                                className="text-[10px] uppercase tracking-[0.15em] text-white bg-[#D95D39] hover:bg-[#c54e2e] px-2 py-1"
                                title="Cambiar"
                              >
                                Cambiar
                              </button>
                              <button
                                type="button"
                                onClick={() => removeDayImage(di, ei, i)}
                                className="text-[10px] uppercase tracking-[0.15em] text-white bg-[#1a1d24]/80 hover:bg-[#1a1d24] px-2 py-1"
                                title="Quitar"
                              >
                                Quitar
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        ))}

        <div className="flex items-center justify-end gap-2 mt-6">
          <Button onClick={persistAll} variant="outline" className="rounded-sm">
            Guardar cambios
          </Button>
        </div>

        {/* Preview modal */}
        <Dialog open={!!previewHtml} onOpenChange={(o) => !o && setPreviewHtml("")}>
          <DialogContent className="bg-white rounded-sm max-w-4xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">Previsualización</DialogTitle>
            </DialogHeader>
            <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: previewHtml }} />
          </DialogContent>
        </Dialog>

        {/* Image picker — must live in the detail-view tree so the button onClick can open it */}
        <ImagePickerModal
          open={picker.open}
          onClose={() => setPicker({ open: false, target: null, defaultAlt: "" })}
          onSelect={handlePickerSelect}
          travefyImages={travefyOriginalImages()}
          defaultAlt={picker.defaultAlt}
        />
      </div>
    );
  }

  // ---------- List view ----------
  return (
    <div className="max-w-6xl mx-auto p-6 lg:p-8">
      <div className="flex items-start justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display text-3xl text-[#0A1128]">Itinerarios</h1>
          <p className="text-sm text-[#5C616A] mt-1">Importa tus itinerarios de Travefy → IA los reescribe en español editorial → los publicas a WordPress como borrador.</p>
        </div>
        <Button
          onClick={() => setImporting(true)}
          className="bg-[#0A1128] hover:bg-[#1c2547] text-white rounded-sm"
          data-testid="itinerary-import-button"
        >
          <Plus className="w-4 h-4 mr-2" />Importar de Travefy
        </Button>
      </div>

      {loading ? (
        <div className="text-center text-[#8A8F98] py-12">Cargando…</div>
      ) : list.length === 0 ? (
        <Card className="p-12 text-center rounded-sm border-dashed border-[#E2DCD0] shadow-none">
          <Map className="w-10 h-10 mx-auto text-[#c19a6b] mb-3" strokeWidth={1.2} />
          <p className="text-[#5C616A]">No tienes ningún itinerario todavía.</p>
          <p className="text-xs text-[#8A8F98] mt-1">Pulsa &quot;Importar de Travefy&quot; arriba.</p>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {list.map((it) => (
            <Card
              key={it.id}
              className="rounded-sm border-[#E2DCD0] shadow-none overflow-hidden hover:border-[#c19a6b] transition-colors cursor-pointer"
              onClick={() => openDetail(it)}
              data-testid={`itinerary-card-${it.id}`}
            >
              {it.hero_image && (
                <img src={it.hero_image} alt="" className="w-full h-36 object-cover" />
              )}
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-display text-lg text-[#0A1128] leading-snug">{it.title || "(sin título)"}</h3>
                  <button
                    type="button"
                    onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      removeItin(it);
                    }}
                    className="text-[#8A8F98] hover:text-[#c54e2e] cursor-pointer p-1 -m-1 relative z-10"
                    data-testid={`itinerary-delete-${it.id}`}
                    aria-label="Borrar itinerario"
                    title="Borrar itinerario"
                  >
                    <Trash2 className="w-4 h-4 pointer-events-none" />
                  </button>
                </div>
                {it.subtitle && (
                  <p className="text-xs italic text-[#5c4e3a] mt-1 line-clamp-2">{it.subtitle}</p>
                )}
                <div className="flex items-center gap-2 mt-3 text-[10px] uppercase tracking-[0.15em] text-[#8A8F98]">
                  {it.duration_days && <span>{it.duration_days} días</span>}
                  {it.price_from && <span>·  {it.price_from}</span>}
                </div>
                <div className="flex items-center gap-1 mt-3">
                  {it.status === "published_draft" ? (
                    <Badge variant="outline" className="rounded-sm text-[10px] border-[#2D6A4F] text-[#2D6A4F] bg-[#2D6A4F]/5">
                      <CheckCircle2 className="w-3 h-3 mr-1" />Draft en WP
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="rounded-sm text-[10px] border-[#8c7c5e] text-[#8c7c5e] bg-[#FFE01B]/10">
                      Pendiente
                    </Badge>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Import modal */}
      <Dialog open={importing} onOpenChange={(o) => !importBusy && setImporting(o)}>
        <DialogContent className="bg-white rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Importar de Travefy</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div>
              <Label className="text-xs">URL pública del itinerario</Label>
              <Input
                value={importUrl}
                onChange={(e) => setImportUrl(e.target.value)}
                placeholder="https://travefy.com/trip/itinerary/..."
                className="mt-1 rounded-sm font-mono text-xs"
                disabled={importBusy}
                data-testid="travefy-url-input"
              />
            </div>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={autoRewrite}
                onChange={(e) => setAutoRewrite(e.target.checked)}
                className="mt-1"
                disabled={importBusy}
              />
              <div>
                <div className="text-sm">Reescribir en estilo editorial con IA</div>
                <div className="text-[11px] text-[#8A8F98]">Claude traduce y adapta el copy al tono Espíritu Travel. Añade 20-30 segundos.</div>
              </div>
            </label>
          </div>
          <DialogFooter className="pt-3">
            <Button
              onClick={submitImport}
              disabled={importBusy}
              className="bg-[#D95D39] hover:bg-[#c54e2e] text-white rounded-sm"
              data-testid="travefy-import-submit"
            >
              {importBusy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Importando…</> : "Importar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation — reliable shadcn AlertDialog, not native window.confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && !deleteBusy && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-white rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl text-[#0A1128]">¿Borrar este itinerario?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#5C616A] text-sm leading-relaxed">
              <strong className="text-[#0A1128]">{deleteTarget?.title}</strong>
              <br /><br />
              Se borra solo del CRM. <strong>No</strong> borra el borrador en WordPress si ya lo publicaste — eso lo haces desde WP Admin.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteBusy} data-testid="delete-cancel-button">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleteBusy}
              className="bg-[#c54e2e] hover:bg-[#a93d20] text-white"
              data-testid="delete-confirm-button"
            >
              {deleteBusy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Borrando…</> : "Borrar definitivamente"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Image picker — used by hero (and soon: day heroes, galleries, accommodations) */}
      <ImagePickerModal
        open={picker.open}
        onClose={() => setPicker({ open: false, target: null, defaultAlt: "" })}
        onSelect={handlePickerSelect}
        travefyImages={travefyOriginalImages()}
        defaultAlt={picker.defaultAlt}
      />
    </div>
  );
}
